import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

const SKELETON_ROWS = ["alpha", "bravo", "charlie"];

type EquipmentAttributes = {
	imei?: string | null;
	serialNumber?: string | null;
};

type EquipmentSummary = {
	brand?: string | null;
	model?: string | null;
	attributes?: EquipmentAttributes | null;
};

type WarrantyEntity = {
	name?: string | null;
};

type WarrantyRepairEntry = {
	id?: string | number | null;
	repairNumber?: string | number | null;
	clientEquipment?: EquipmentSummary | null;
	warrantyEntity?: WarrantyEntity | null;
	problemDescription?: string | null;
};

type WarrantyListInWarrantyTableProps = {
	repairs?: WarrantyRepairEntry[];
	loading?: boolean;
	onEntrance: (repair: WarrantyRepairEntry) => void;
};

const formatEquipmentName = (equipment?: EquipmentSummary | null) => {
	if (!equipment) {
		return "-";
	}

	const brand = equipment.brand?.trim() ?? "";
	const model = equipment.model?.trim() ?? "";
	const label = `${brand} ${model}`.trim();
	return label.length > 0 ? label : "-";
};

const formatImei = (equipment?: EquipmentSummary | null) => {
	const attributes = equipment?.attributes;
	const imei = attributes?.imei ?? "";
	const serial = attributes?.serialNumber ?? "";
	const value = imei.length > 0 ? imei : serial;
	return value.length > 0 ? value : "-";
};

export function WarrantyListInWarrantyTable({
	repairs = [],
	loading = false,
	onEntrance,
}: WarrantyListInWarrantyTableProps) {
	const renderSkeleton = () => (
		<TableBody>
			{SKELETON_ROWS.map((rowKey) => (
				<TableRow key={`warranty-in-${rowKey}`} className="animate-pulse">
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/70" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-40 rounded bg-muted/60" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-28 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-40 rounded bg-muted/40" />
					</TableCell>
					<TableCell className="text-right">
						<div className="ml-auto h-8 w-24 rounded bg-muted/40" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	const renderEmpty = () => (
		<TableBody>
			<TableRow>
				<TableCell
					colSpan={6}
					className="py-10 text-center text-sm text-muted-foreground"
				>
					Sem reparações em garantia.
				</TableCell>
			</TableRow>
		</TableBody>
	);

	return (
		<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/50">
						<TableHead className="w-[100px]">N. Ficha</TableHead>
						<TableHead className="w-[300px]">Equipamento</TableHead>
						<TableHead className="w-[160px]">Imei/SN</TableHead>
						<TableHead className="w-[120px]">Entidade</TableHead>
						<TableHead>Anomalia</TableHead>
						<TableHead className="w-[120px]" />
					</TableRow>
				</TableHeader>
				{loading ? (
					renderSkeleton()
				) : repairs.length > 0 ? (
					<TableBody>
						{repairs.map((repair) => {
							const rowKey = repair.id ?? repair.repairNumber ?? "repair-row";
							const equipmentName = formatEquipmentName(repair.clientEquipment);

							return (
								<TableRow key={rowKey}>
									<TableCell>{repair.repairNumber ?? "-"}</TableCell>
									<TableCell>{equipmentName}</TableCell>
									<TableCell>{formatImei(repair.clientEquipment)}</TableCell>
									<TableCell>{repair.warrantyEntity?.name ?? "-"}</TableCell>
									<TableCell>{repair.problemDescription ?? "-"}</TableCell>
									<TableCell className="text-right">
										<Button
											size="sm"
											variant="secondary"
											onClick={() => onEntrance(repair)}
										>
											Dar Entrada
										</Button>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				) : (
					renderEmpty()
				)}
			</Table>
		</div>
	);
}
